import React from 'react';
import { FileText, Scale, AlertTriangle, Users, Shield, Globe } from 'lucide-react';

const TermsOfService: React.FC = () => {
  const sections = [
    {
      icon: <FileText className="h-8 w-8" />,
      title: 'Service Agreement',
      content: [
        'These terms govern your use of Infurasoft digital services and solutions',
        'By using our services, you agree to be bound by these terms and conditions',
        'We reserve the right to modify these terms at any time with notice',
        'Continued use of services after changes constitutes acceptance of new terms'
      ]
    },
    {
      icon: <Scale className="h-8 w-8" />,
      title: 'Service Scope',
      content: [
        'We provide app development, web development, SEO, and marketing consultancy services',
        'Services are provided according to project specifications and agreed timelines',
        'We maintain professional standards and industry best practices',
        'Quality assurance and testing are integral parts of our service delivery'
      ]
    },
    {
      icon: <AlertTriangle className="h-8 w-8" />,
      title: 'Limitations & Liability',
      content: [
        'Our liability is limited to the amount paid for services rendered',
        'We are not liable for indirect, incidental, or consequential damages',
        'Client is responsible for providing accurate project requirements',
        'Force majeure events may affect service delivery timelines'
      ]
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: 'Client Responsibilities',
      content: [
        'Provide accurate and complete project information and requirements',
        'Timely feedback and approval during project development phases',
        'Payment according to agreed terms and project milestones',
        'Compliance with applicable laws and regulations in their industry'
      ]
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: 'Intellectual Property',
      content: [
        'Client retains ownership of their existing intellectual property',
        'New intellectual property created for client projects belongs to the client',
        'We retain rights to our proprietary methodologies and frameworks',
        'Open source components are subject to their respective licenses'
      ]
    },
    {
      icon: <Globe className="h-8 w-8" />,
      title: 'Governing Law',
      content: [
        'These terms are governed by the laws of Pakistan',
        'Disputes will be resolved through arbitration or court proceedings',
        'Both parties agree to attempt amicable resolution before legal action',
        'Jurisdiction for legal matters is in Lahore, Pakistan'
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 via-green-700 to-emerald-800 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-center">
            <div className="mb-4">
              <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-green-100 border border-white/30">
                📋 Legal Terms
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 leading-tight">
              Terms of <span className="text-green-300 bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">Service</span>
            </h1>
            <p className="text-lg lg:text-xl mb-8 text-green-100 leading-relaxed max-w-4xl mx-auto">
              Please read these terms carefully before using our services. These terms outline the legal agreement between you and Infurasoft.
            </p>
            
            {/* Quick Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">Legal</div>
                <div className="text-green-200 text-sm">Agreement</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">Clear</div>
                <div className="text-green-200 text-sm">Terms</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">Fair</div>
                <div className="text-green-200 text-sm">Practices</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">Protection</div>
                <div className="text-green-200 text-sm">Both Parties</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Last Updated */}
      <section className="py-12 bg-gradient-to-br from-white via-gray-50/50 to-green-50/30 dark:from-gray-900 dark:via-gray-800/50 dark:to-green-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 rounded-full text-sm font-medium mb-6">
              📅 Last Updated: December 2024
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Our <span className="text-green-600 dark:text-green-400">Service Terms</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              These Terms of Service ("Terms") govern your use of Infurasoft's digital services and solutions. By engaging our services, you agree to these terms.
            </p>
          </div>
        </div>
      </section>

      {/* Terms Sections */}
      <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-green-50/30 dark:from-gray-800 dark:via-gray-900 dark:to-green-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {sections.map((section, index) => (
              <div key={index} className="group relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-100/50 dark:border-gray-700/50 hover:border-green-200 dark:hover:border-green-600 hover:-translate-y-1 hover:scale-102 mx-4">
                {/* Header with Gradient */}
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white relative overflow-hidden">
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/5 transition-colors duration-300"></div>
                  
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute inset-0" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='20' cy='20' r='2'/%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center mb-3">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl mr-3 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                        {section.icon}
                      </div>
                      <h3 className="text-xl font-bold">{section.title}</h3>
                    </div>
                  </div>
                  
                  {/* Floating Elements */}
                  <div className="absolute top-3 right-3 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-white text-xs animate-pulse">
                    ⭐
                  </div>
                  <div className="absolute bottom-3 left-3 w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-white text-xs animate-bounce">
                    📋
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="space-y-3">
                    {section.content.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-start group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                        <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full mr-3 mt-2 flex-shrink-0 group-hover:scale-125 transition-transform duration-300"></div>
                        <span className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-green-500/20 to-emerald-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Terms */}
      <section className="py-12 bg-gradient-to-br from-white via-gray-50/50 to-orange-50/30 dark:from-gray-900 dark:via-gray-800/50 dark:to-orange-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-200 rounded-full text-sm font-medium mb-6">
              💳 Payment Terms
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              <span className="text-orange-600 dark:text-orange-400">Payment & Billing</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Clear and transparent payment terms to ensure smooth project execution and mutual understanding.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-gray-100/50 dark:border-gray-700/50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Payment Schedule</h3>
                  <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                    <li>• 50% upfront payment for project initiation</li>
                    <li>• 30% milestone payment upon completion</li>
                    <li>• 20% final payment upon project delivery</li>
                    <li>• Payment terms: Net 15 days</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Accepted Methods</h3>
                  <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                    <li>• Bank transfers and wire transfers</li>
                    <li>• Credit card payments</li>
                    <li>• PayPal and digital wallets</li>
                    <li>• Cryptocurrency (by agreement)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-purple-50/30 dark:from-gray-800 dark:via-gray-900 dark:to-purple-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 rounded-full text-sm font-medium mb-6">
              📞 Legal Inquiries
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Questions About <span className="text-purple-600 dark:text-purple-400">Terms?</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              If you have any questions about these Terms of Service, please contact our legal team.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-gray-100/50 dark:border-gray-700/50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Legal Department</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">For terms and legal inquiries:</p>
                  <a href="mailto:legal@infurasoft.com" className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-semibold">
                    legal@infurasoft.com
                  </a>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">General Contact</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">For general questions:</p>
                  <a href="mailto:hello@infurasoft.com" className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-semibold">
                    hello@infurasoft.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 bg-gradient-to-br from-green-600 via-emerald-700 to-teal-800 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <span className="inline-block px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-green-100 border border-white/30 mb-6">
              🤝 Fair Partnership
            </span>
          </div>
          
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Clear Terms for <span className="text-green-300 bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">Better Partnership</span>
          </h2>
          <p className="text-xl lg:text-2xl text-green-100 mb-12 max-w-4xl mx-auto leading-relaxed">
            We believe in transparent, fair terms that protect both parties and foster successful long-term partnerships.
          </p>
          
          {/* Trust Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Transparent</div>
              <div className="text-green-200 text-sm">Terms</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Fair</div>
              <div className="text-green-200 text-sm">Practices</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Mutual</div>
              <div className="text-green-200 text-sm">Protection</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
